import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import {
	FeatureAppDefinition,
	FeatureServices,
	Logger,
} from '@feature-hub/core';
import { HistoryServiceV2 } from '@feature-hub/history-service';
import { ReactFeatureApp } from '@feature-hub/react';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { ServerRequestV1 } from '@feature-hub/server-request';
import { ApiKeyProviderV1 } from '@volkswagen-onehub/api-key-provider';
import { BreadcrumbServiceV1 } from '@volkswagen-onehub/breadcrumb-service';
import { DisclaimerManagerV1 } from '@volkswagen-onehub/disclaimer-manager';
import { FaContentServiceV1 } from '@volkswagen-onehub/fa-content-service';
import { FeatureAppMemoryStoreV1 } from '@volkswagen-onehub/feature-app-memory-store';
import { FeatureappServiceV1Impl } from '@volkswagen-onehub/featureapp-service';
import { LayerManagerV25 } from '@volkswagen-onehub/layer-manager';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { PageInfoServiceV1 } from '@volkswagen-onehub/page-info-service';
import { ServiceConfigProviderV1 } from '@volkswagen-onehub/service-config-provider';
import { TrackingManagerV1 } from '@volkswagen-onehub/tracking-service';
import { UniversalState } from '@volkswagen-onehub/vw-starter-feature-app-support';
import React from 'react';
import { App } from './app';
import { IOfertasContext, OfertasContext } from './context/ofertas-context';
import { saveFeatureServices } from './services/ofertas-service/dataUrls/get-data-url';
import { ofertasServiceDefinition } from './services/ofertas-service/ofertas-service';
import { OfertasV2 } from './services/ofertas-service/v2';
//import type {BffResponse} from '@esofertasv2/bff';

export interface Esofertasv2FeatureAppDependencies extends FeatureServices {
	readonly 'api-key-provider'?: ApiKeyProviderV1;
	readonly 'disclaimer-manager': DisclaimerManagerV1;
	readonly 'fa-content-service'?: FaContentServiceV1;
	readonly 'ofertas-feature-service': OfertasV2;
	readonly 'layer-manager': LayerManagerV25;
	readonly 'locale-service': LocaleServiceV1;
	readonly 's2:history': HistoryServiceV2;
	readonly 's2:logger'?: Logger;
	readonly 's2:server-request'?: ServerRequestV1;
	readonly 'service-config-provider': ServiceConfigProviderV1;
	readonly 'page-info-service': PageInfoServiceV1;
	readonly 'breadcrumb-service': BreadcrumbServiceV1;
	readonly 's2:serialized-state-manager': SerializedStateManagerV1;
	readonly 's2:async-ssr-manager': AsyncSsrManagerV1;
	readonly 'feature-app-memory-store': FeatureAppMemoryStoreV1<
		UniversalState<any>
	>;
	readonly 'tracking': TrackingManagerV1;
}

export const featureAppDefinition: FeatureAppDefinition<
	ReactFeatureApp,
	Esofertasv2FeatureAppDependencies
> = {
	dependencies: {
		featureServices: {
			'ofertas-feature-service': '^2.0.0',
			'disclaimer-manager': '^1.1.0',
			'feature-app-memory-store': '^1.0.0',
			'layer-manager': '^2.4.0',
			'locale-service': '^1.0.0',
			'navigation-service': '^1.1.0',
			's2:history': '^2.0.0',
			's2:serialized-state-manager': '^1.0.0',
			'service-config-provider': '^1.0.0',
			'featureapp-service': '^1.0.0',
			'tracking': '^1.0.0',
		},
	},
	optionalDependencies: {
		featureServices: {
			'page-info-service': '^1.0.0',
			'breadcrumb-service': '^1.0.0',
			'api-key-provider': '^1.0.0',
			'fa-content-service': '^1.0.0',
			's2:async-ssr-manager': '^1.0.0',
			's2:logger': '^1.0.0',
			's2:server-request': '^1.0.0',
		},
	},
	ownFeatureServiceDefinitions: [ofertasServiceDefinition],

	create: ({ config = {}, baseUrl, featureServices, featureAppId }) => ({
		render() {
			const env = { baseUrl, config, featureServices, featureAppId };

			saveFeatureServices(env.featureServices);

			const serviceConfigProvider = env.featureServices[
				'service-config-provider'
			] as ServiceConfigProviderV1;
			const layerManager = env.featureServices[
				'layer-manager'
			] as LayerManagerV25;
			const contentService = env.featureServices[
				'fa-content-service'
			] as FaContentServiceV1;
			const logger = env.featureServices['s2:logger'] as Logger;
			const historyService = env.featureServices[
				's2:history'
			] as HistoryServiceV2;
			const breacrumbService = env.featureServices[
				'breadcrumb-service'
			] as BreadcrumbServiceV1;
			const pageInfoService = env.featureServices[
				'page-info-service'
			] as PageInfoServiceV1;
			const disclaimerManager = env.featureServices[
				'disclaimer-manager'
			] as DisclaimerManagerV1;
			const ofertasService = env.featureServices[
				'ofertas-feature-service'
			] as OfertasV2;
			const featureAppService = env.featureServices[
				'featureapp-service'
			] as FeatureappServiceV1Impl;
			const apiKeyProvider = env.featureServices[
				'api-key-provider'
			] as ApiKeyProviderV1;
			const trackingService = env.featureServices['tracking'];

			return (
				<OfertasContext.Provider
					value={
						{
							featureAppId,
							featureServices,
							serviceConfigProvider,
							layerManager,
							logger,
							historyService,
							breacrumbService,
							pageInfoService,
							disclaimerManager,
							contentService,
							ofertasService,
							featureAppService,
							apiKeyProvider,
							trackingService,
						} as IOfertasContext
					}
				>
					<App />
				</OfertasContext.Provider>
			);
		},
	}),

	// create: (env) => {
	// 	const {config, baseUrl, featureServices} = env;
	// 	//let initialData: OffersState = { offers: [] };

	// 	const {
	// 		's2:serialized-state-manager': serializedStateManager, // TODO necessary for SSR support. Remove otherwise.
	// 		'feature-app-memory-store': memoryStore,
	// 		's2:logger': logger = console,
	// 		's2:server-request': serverRequest, // TODO necessary for SSR support. Remove otherwise.
	// 		's2:async-ssr-manager': asyncSsrManager, // TODO necessary for SSR support. Remove otherwise.
	// 		'fa-content-service': contentService,
	// 	} = featureServices;

	// 	/*
	// // initialize the mock server and mock ids
	// initializeFetchJson('esofertasv2', logger, config);

	// // retrieve serialized state on client and store it on server
	// manageBffSerializationAndCaching(
	//   serializedStateManager,
	//   memoryStore,
	//   logger,
	// );

	// // assemble common bff options
	// // TODO extract to support
	// const commonBffFetchOptions = createBffFetchOptions(
	//   baseUrl,
	//   config || {},
	//   featureServices,
	// );

	// const loadStatus = memoryStore.getState()?.status || 'loading';

	// const bffLoadComplete = loadStatus === 'loaded' || loadStatus === 'error';

	// const bffPromise = bffLoadComplete
	//   ? undefined
	//   : fetchBffData('/esofertasv2', commonBffFetchOptions).then((response) => {
	//       if (response) {
	//         saveBffResponse(memoryStore, response);
	//       }

	//       return undefined;
	//     });

	// // TODO necessary for SSR support. Remove otherwise.
	// if (serverRequest && bffPromise) {
	//   asyncSsrManager?.scheduleRerender(bffPromise);
	// }
	// */

	// 	const featureAppContent = contentService?.content;

	// 	return {
	// 		//loadingPromise: serverRequest || bffLoadComplete ? undefined : bffPromise,

	// 		// eslint-disable-next-line react/display-name
	// 		render: () => {
	// 			/*
	//     const state = memoryStore.getState();
	//     if (state?.status !== 'loaded') {
	//       return null;
	//     }
	//     */
	// 			console.log('🚀 ~ Index ~ env', env);
	// 			return (
	// 				<FeatureServicesContext.Provider value={featureServices}>
	// 					<ThemeProvider theme="main">
	// 						<App content={featureAppContent} />
	// 					</ThemeProvider>
	// 				</FeatureServicesContext.Provider>
	// 			);
	// 		},
	// 	};
	// },
};

export default featureAppDefinition;
